import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { Quote } from '../types';
import StatusBadge from './StatusBadge';
import Icon from '../../../components/AppIcon';
import { Checkbox } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';
import { quoteService } from '../../../services/quoteService';
import { supabase } from '../../../lib/supabase';

interface QuoteDataGridProps {
  quotes: Quote[];
  selectedQuotes: string[];
  onSelectQuote: (quoteId: string) => void;
  onSelectAll: (selected: boolean) => void;
  userRole: 'manager' | 'sales-rep';
  onSort: (column: string) => void;
  sortColumn: string;
  sortDirection: 'asc' | 'desc';
  onQuotesChange?: () => void;
}

const QuoteDataGrid: React.FC<QuoteDataGridProps> = ({
  quotes,
  selectedQuotes,
  onSelectQuote,
  onSelectAll,
  userRole,
  onSort,
  sortColumn,
  sortDirection,
  onQuotesChange,
}) => {
  const navigate = useNavigate();
  const [loadingAction, setLoadingAction] = useState<{ id: string; action: string } | null>(null);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-EU', {
      style: 'currency',
      currency: 'EUR',
    }).format(value);
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(date);
  };

  const allSelected = quotes.length > 0 && selectedQuotes.length === quotes.length;
  const someSelected = selectedQuotes.length > 0 && selectedQuotes.length < quotes.length;

  const renderSortIcon = (column: string) => {
    if (sortColumn !== column) {
      return <Icon name="ChevronsUpDown" size={14} className="text-text-secondary" />;
    }
    return sortDirection === 'asc' ? (
      <Icon name="ChevronUp" size={14} className="text-primary" />
    ) : (
      <Icon name="ChevronDown" size={14} className="text-primary" />
    );
  };

  const handleEditQuote = (quoteId: string) => {
    navigate(`/quote-editor-interface/${quoteId}`);
  };

  const handleDuplicateQuote = async (quoteId: string, referenceCode: string) => {
    try {
      setLoadingAction({ id: quoteId, action: 'duplicate' });

      // Generate new reference code using Supabase function
      const currentYear = new Date().getFullYear();
      const yearPrefix = `Q-${currentYear}-`;

      const { data: newReference, error: rpcError } = await supabase.rpc(
        'generate_next_quote_reference',
        { year_prefix: yearPrefix }
      );

      if (rpcError) {
        throw new Error(`Failed to generate reference code: ${rpcError.message}`);
      }

      if (!newReference) {
        throw new Error('No reference code generated');
      }

      // Duplicate the quote with new reference code
      await quoteService.duplicateQuote(quoteId, newReference);

      // Show success message
      alert(`Quote duplicated successfully! New reference: ${newReference}`);

      // Refresh the quotes list
      if (onQuotesChange) {
        onQuotesChange();
      } else {
        window.location.reload();
      }
    } catch (error) {
      console.error('Error duplicating quote:', error);
      alert(error instanceof Error ? error.message : 'Failed to duplicate quote');
    } finally {
      setLoadingAction(null);
    }
  };

  const handleDeleteQuote = async (quoteId: string, referenceCode: string) => {
    if (!window.confirm(`Are you sure you want to delete quote ${referenceCode}? This action cannot be undone.`)) {
      return;
    }

    try {
      setLoadingAction({ id: quoteId, action: 'delete' });
      await quoteService.delete(quoteId);

      // Show success message
      alert(`Quote ${referenceCode} deleted successfully!`);

      // Refresh the quotes list
      if (onQuotesChange) {
        onQuotesChange();
      } else {
        window.location.reload();
      }
    } catch (error) {
      console.error('Error deleting quote:', error);
      alert(error instanceof Error ? error.message : 'Failed to delete quote');
    } finally {
      setLoadingAction(null);
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted border-b border-border">
            <tr>
              <th className="w-12 px-4 py-3">
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={(e) => onSelectAll(e.target.checked)}
                  aria-label="Select all quotes"
                />
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => onSort('referenceCode')}
                  className="flex items-center gap-2 text-sm font-semibold text-text-primary hover:text-primary transition-colors"
                >
                  Reference
                  {renderSortIcon('referenceCode')}
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => onSort('customerName')}
                  className="flex items-center gap-2 text-sm font-semibold text-text-primary hover:text-primary transition-colors"
                >
                  Customer
                  {renderSortIcon('customerName')}
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <span className="text-sm font-semibold text-text-primary">Project</span>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => onSort('status')}
                  className="flex items-center gap-2 text-sm font-semibold text-text-primary hover:text-primary transition-colors"
                >
                  Status
                  {renderSortIcon('status')}
                </button>
              </th>
              <th className="px-4 py-3 text-right">
                <button
                  onClick={() => onSort('totalValue')}
                  className="flex items-center justify-end gap-2 text-sm font-semibold text-text-primary hover:text-primary transition-colors ml-auto"
                >
                  Total Value
                  {renderSortIcon('totalValue')}
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <span className="text-sm font-semibold text-text-primary">User</span>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => onSort('createdDate')}
                  className="flex items-center gap-2 text-sm font-semibold text-text-primary hover:text-primary transition-colors"
                >
                  Quote Date
                  {renderSortIcon('createdDate')}
                </button>
              </th>
              <th className="px-4 py-3 text-center">
                <span className="text-sm font-semibold text-text-primary">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {quotes.map((quote) => {
              const isLoading = loadingAction?.id === quote.id;
              const loadingActionType = loadingAction?.action;

              return (
                <tr
                  key={quote.id}
                  className="hover:bg-muted/50 transition-colors group"
                >
                  <td className="px-4 py-3">
                    <Checkbox
                      checked={selectedQuotes.includes(quote.id)}
                      onChange={() => onSelectQuote(quote.id)}
                      aria-label={`Select quote ${quote.referenceCode}`}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <Link
                      to={`/quote-editor-interface/${quote.id}`}
                      className="text-sm font-medium text-primary hover:underline"
                    >
                      {quote.referenceCode}
                    </Link>
                    {quote.isModified && (
                      <Icon
                        name="AlertCircle"
                        size={14}
                        className="inline-block ml-2 text-warning"
                        title="Quote has unsaved modifications"
                      />
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-text-primary">{quote.companyName || quote.customerName}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-text-primary">{quote.projectTitle}</span>
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={quote.status} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="text-sm font-medium text-text-primary">
                      {formatCurrency(quote.totalValue)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-text-primary">{quote.latestUser || quote.assignedSalesRep}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-text-primary">{formatDate(new Date(quote.createdDate))}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Edit"
                        onClick={() => handleEditQuote(quote.id)}
                        disabled={isLoading}
                        title="Edit quote"
                        className="hover:bg-primary/10 hover:text-primary"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName={isLoading && loadingActionType === 'duplicate' ? 'Loader' : 'Copy'}
                        onClick={() => handleDuplicateQuote(quote.id, quote.referenceCode)}
                        disabled={isLoading}
                        title="Duplicate quote"
                        className="hover:bg-primary/10 hover:text-primary"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName={isLoading && loadingActionType === 'delete' ? 'Loader' : 'Trash2'}
                        onClick={() => handleDeleteQuote(quote.id, quote.referenceCode)}
                        disabled={isLoading}
                        title="Delete quote"
                        className="hover:bg-error/10 hover:text-error"
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default QuoteDataGrid;